import { el } from './dom';
import { achievementsApi, type Achievement } from './game-api';
import { gicon } from './ui/icons';
import { achievementBadge, TIER_COLOR, TIER_LABEL } from './views/achievements';

/**
 * "You won an award" — a farm-screen reward card.
 *
 * WHY A CARD AND NOT A TOAST (dom.ts already has toasts):
 * the toast stack is built for *action feedback and errors* — it strips emoji,
 * auto-drops after 2.6s, is `pointer-events-none` (so it cannot hold a button),
 * dedupes identical text into "… x3", and caps at three. Every one of those
 * behaviours is wrong for an award: the player must be able to MISS it and still
 * find it (it waits for a click, not a timer), it has to carry the badge art and
 * two 44px controls, and "Bronze: Woodcutter" must never be collapsed into a
 * counter next to "Silver: Woodcutter". So this is a distinct, non-blocking card
 * that lives inside the farm view — and the toast system is left alone.
 *
 * HOW UNLOCKS ARE LEARNED (checked against the backend, not invented):
 * `backend/src/game/achievements/achievements.controller.ts` exposes exactly ONE
 * route, `GET /achievements`, and `AchievementsService.list()` runs `check()`
 * first — so *fetching the list is what unlocks things*. There is no
 * "unlocked since you last looked" field, no socket event, and no `seen` flag on
 * the server. What does exist is `unlocked` + `unlockedAt` per achievement.
 * Therefore the "new since last time" set is computed CLIENT-side: the farm
 * fetches the list on arrival (which also performs the server-side unlock pass,
 * so awards earned in the arena / dungeon / offline are minted right there) and
 * anything unlocked that this browser has not acknowledged is shown.
 *
 * Acknowledgement is persisted in localStorage, keyed per user id so two accounts
 * on one browser do not acknowledge each other's awards. It is a UI-seen marker,
 * not game state — losing it costs one re-shown card, never a reward.
 */

const ACK_PREFIX = 'tb.awards.ack.';
/** Beyond this many at once, one summary card instead of a queue. */
export const SUMMARY_THRESHOLD = 3;
const TIER_RANK: Record<string, number> = { bronze: 0, silver: 1, gold: 2, legendary: 3 };

function ackKey(userId: string): string { return ACK_PREFIX + userId; }

export function loadAck(userId: string): Set<string> {
  try {
    const raw = localStorage.getItem(ackKey(userId));
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []);
  } catch { return new Set(); }
}

function writeAck(userId: string, ids: Set<string>): void {
  try { localStorage.setItem(ackKey(userId), JSON.stringify([...ids])); } catch { /* private mode */ }
}

/** Mark awards as seen; they never pop again on this browser. */
export function acknowledge(userId: string, ids: string[]): void {
  const set = loadAck(userId);
  for (const id of ids) set.add(id);
  writeAck(userId, set);
}

/** Has this browser ever recorded an acknowledgement for this user? */
export function hasAckRecord(userId: string): boolean {
  try { return localStorage.getItem(ackKey(userId)) !== null; } catch { return false; }
}

/**
 * Unlocked awards this browser has not shown yet, newest first.
 *
 * FIRST RUN is deliberately silent: a player who already owns forty awards before
 * this feature existed would otherwise be told all forty are "new". With no ack
 * record at all we seed the whole unlocked set as acknowledged and show nothing —
 * from that moment on every genuinely new unlock pops, including ones won while
 * the player was on another screen or logged out.
 */
export function pendingAwards(userId: string, list: Achievement[]): Achievement[] {
  const unlocked = list.filter((a) => a.unlocked);
  if (!hasAckRecord(userId)) {
    writeAck(userId, new Set(unlocked.map((a) => a.id)));
    return [];
  }
  const ack = loadAck(userId);
  return unlocked
    .filter((a) => !ack.has(a.id))
    .sort((x, y) => (y.unlockedAt ?? '').localeCompare(x.unlockedAt ?? ''));
}

/** The loudest of a batch — what a summary card wears. */
export function topAward(awards: Achievement[]): Achievement {
  return awards.reduce((best, a) => ((TIER_RANK[a.tier] ?? 0) > (TIER_RANK[best.tier] ?? 0) ? a : best), awards[0]);
}

/**
 * Open the Awards tab.
 *
 * The tab switcher (`setTab`) is private to `engine/bootstrap.ts`, which this
 * agent does not own, so the nav button is clicked instead of calling it. That
 * keeps the active-tab styling, audio view and teardown all going through the one
 * code path that already does them. If bootstrap ever exports a navigation helper,
 * this should call it directly.
 */
function openAwardsTab(): void {
  const btn = document.querySelector<HTMLButtonElement>('nav.tb-nav button[aria-label="Awards"]');
  btn?.click();
}

export interface AwardPopupOpts {
  /** Overridable for tests; defaults to clicking the Awards tab button. */
  onOpenAwards?: () => void;
}

/**
 * Mount the card(s) for `awards` into `host` and return a teardown.
 *
 * ONE card is on screen at a time — a wall of them would cover the farm, which is
 * the opposite of a reward. Two or three unlocks queue (dismiss reveals the next);
 * `SUMMARY_THRESHOLD` or more collapse into a single "N new awards" card, because
 * clearing eight cards one at a time is a chore, not a celebration.
 */
export function mountAwardPopup(host: HTMLElement, userId: string, awards: Achievement[], opts: AwardPopupOpts = {}): () => void {
  if (!awards.length) return () => {};
  const openAwards = opts.onOpenAwards ?? openAwardsTab;
  const summary = awards.length >= SUMMARY_THRESHOLD;
  const queue = summary ? [topAward(awards)] : [...awards];
  let card: HTMLElement | null = null;
  let index = 0;

  function ackAll(): void { acknowledge(userId, awards.map((a) => a.id)); }

  function close(): void { card?.remove(); card = null; }

  function next(): void {
    close();
    if (index >= queue.length) return;
    const a = queue[index++];
    card = buildCard(a, summary ? awards.length : 0, {
      onDismiss: () => {
        // Dismissing the SUMMARY acknowledges the whole batch — it stood for all
        // of them, so re-popping the rest one by one would defeat the summary.
        if (summary) { ackAll(); close(); return; }
        acknowledge(userId, [a.id]);
        next();
      },
      onOpen: () => { ackAll(); close(); openAwards(); },
    });
    host.appendChild(card);
  }
  next();

  return close;
}

function buildCard(
  a: Achievement,
  batchCount: number,
  handlers: { onDismiss: () => void; onOpen: () => void },
): HTMLElement {
  const ring = TIER_COLOR[a.tier] ?? '#c9a84c';
  const title = batchCount ? `${batchCount} new awards` : a.name;
  const sub = batchCount ? `Including “${a.name}”` : a.description;

  // Buttons: `min-h-11` is 44px exactly (Tailwind 11 = 2.75rem) — the tap-target
  // floor. The card is capped at 320px but never wider than the viewport, so it
  // still fits a 320px phone with the 12px insets.
  const btn = (label: string, primary: boolean, onClick: () => void, icon?: Parameters<typeof gicon>[0]) =>
    el('button',
      'flex-1 min-h-11 px-3 rounded-md text-[12px] inline-flex items-center justify-center gap-1.5 transition border '
      + (primary ? 'border-gold text-gold bg-gold/15 hover:bg-gold/25' : 'border-border text-text-dim hover:text-text'),
      { onClick, type: 'button' }, icon ? gicon(icon, 12) : null, label);

  const card = el('div',
    // `pointer-events-auto` on the card only: the wrapper is inside the farm view,
    // and the card must be clickable while never blocking the world around it.
    'tb-award-popup absolute top-3 right-3 z-40 w-[min(320px,calc(100vw-1.5rem))] rounded-lg border bg-surface/95 shadow-lift p-3 flex flex-col gap-2.5 pointer-events-auto',
    { style: `border-color:${ring};box-shadow:0 0 16px ${ring}44` },
    el('div', 'flex items-start gap-3', {},
      achievementBadge(a, 48),
      el('div', 'flex-1 min-w-0', {},
        el('div', 'text-[10px] uppercase tracking-wider text-leaf inline-flex items-center gap-1', {}, gicon('trophy', 11), 'Award unlocked'),
        el('div', 'text-sm font-medium text-text truncate', {}, title),
        el('div', 'text-[11px] leading-snug text-text-dim', {}, sub),
        el('div', 'text-[10px] uppercase tracking-wider mt-0.5', { style: `color:${ring}` }, TIER_LABEL[a.tier] ?? a.tier))),
    el('div', 'flex gap-2', {},
      btn('Dismiss', false, handlers.onDismiss, 'xmark'),
      btn('See awards', true, handlers.onOpen, 'trophy')));
  // Attributes, not properties: `el()` assigns through Object.assign, and
  // `aria-live` is not a DOM property — it would become a silent expando.
  card.setAttribute('role', 'status');
  card.setAttribute('aria-live', 'polite');
  return card;
}

/**
 * The farm's entry point: fetch (which also runs the server's unlock pass), then
 * pop whatever is new. Silent on failure — a missed card must never break the farm.
 */
export async function showFarmAwards(host: HTMLElement, userId: string, opts: AwardPopupOpts = {}): Promise<() => void> {
  try {
    const res = await achievementsApi.list();
    // The view may have been torn down while the request was in flight.
    if (!host.isConnected) return () => {};
    return mountAwardPopup(host, userId, pendingAwards(userId, res.achievements), opts);
  } catch { return () => {}; }
}
